import React, { useState, useEffect } from 'react';
import { 
  FiSearch, FiFilter, FiCalendar, FiLifeBuoy, FiClock, 
  FiCheckCircle, FiXCircle, FiPlay, FiFileText, FiImage, FiChevronRight, FiTrash2, FiX 
} from 'react-icons/fi';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function HelpdeskTicketsPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // ตรวจสอบสิทธิ์ว่าใช่ไอที (Role: it / username: it) หรือเจ้าของระบบ (Role: owner / username: owner) หรือไม่
  const isITOrOwner = user?.role === 'owner' || user?.role === 'it' || user?.username === 'it' || user?.username === 'owner';

  // ฟิลเตอร์และค้นหา
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // ตัวแปรตั๋วที่กำลังกดดูรายละเอียด
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [screenshotModal, setScreenshotModal] = useState(null); // ดูรูปขนาดใหญ่

  // ฟิลด์สำหรับแก้ไขตั๋ว (ฝั่งไอที)
  const [newStatus, setNewStatus] = useState('');
  const [itNotes, setItNotes] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setIsLoading(true);
    try {
      // 🚀 พยายามดึงจากโฮสต์เซิร์ฟเวอร์
      const res = await fetch(`${API_URL}?action=get_tickets`);
      const data = await res.json();
      if (data.success && data.data) {
        setTickets(data.data);
      } else {
        throw new Error('API not supported');
      }
    } catch (err) {
      // 🚀 โหลดสำรองจาก LocalStorage หากโฮสต์ยังไม่มี API ตั๋ว
      console.log('Loading tickets from LocalStorage.');
      const saved = localStorage.getItem('helpdesk_tickets');
      if (saved) {
        setTickets(JSON.parse(saved));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // กรองตั๋วตามเงื่อนไข
  const filteredTickets = tickets.filter(ticket => {
    const safeSubject = ticket.subject || '';
    const safeReporter = ticket.reporterName || '';
    const isSearchMatch = 
      safeSubject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      safeReporter.toLowerCase().includes(searchTerm.toLowerCase());

    const isStatusMatch = statusFilter === 'all' ? true : ticket.status === statusFilter;
    const isCategoryMatch = categoryFilter === 'all' ? true : ticket.category === categoryFilter;

    return isSearchMatch && isStatusMatch && isCategoryMatch;
  });

  // คำนวณยอดสรุป (Metrics)
  const totalCount = filteredTickets.length;
  const pendingCount = filteredTickets.filter(t => t.status === 'pending').length;
  const inProgressCount = filteredTickets.filter(t => t.status === 'in_progress').length;
  const resolvedCount = filteredTickets.filter(t => t.status === 'resolved').length;

  const getPriorityBadge = (p) => {
    switch (p) {
      case 'low': return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400">🟢 ต่ำ</span>;
      case 'medium': return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400">🟡 ปานกลาง</span>;
      case 'high': return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400">🟠 สูง</span>;
      case 'critical': return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 animate-pulse">🔴 วิกฤต</span>;
      default: return p;
    }
  };

  const getStatusBadge = (s) => {
    switch (s) {
      case 'pending': return <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700 dark:bg-yellow-950/20 dark:text-yellow-400 flex items-center gap-1"><FiClock /> รอรับเรื่อง</span>;
      case 'in_progress': return <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400 flex items-center gap-1"><FiPlay /> กำลังดำเนินงาน</span>;
      case 'resolved': return <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-950/20 dark:text-green-400 flex items-center gap-1"><FiCheckCircle /> แก้ไขเสร็จสิ้น</span>;
      case 'cancelled': return <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-950/20 dark:text-red-400 flex items-center gap-1"><FiXCircle /> ยกเลิกตั๋ว</span>;
      default: return s;
    }
  };

  const getCategoryLabel = (c) => {
    const cats = {
      'bug': '🐛 บั๊กระบบ',
      'feature': '✨ ขอฟีเจอร์',
      'hardware': '🔌 ฮาร์ดแวร์',
      'access': '🔑 บัญชี/สิทธิ์',
      'general': '❓ คำถามทั่วไป'
    };
    return cats[c] || c;
  };

  // เปิดดูรายละเอียดของตั๋ว
  const handleOpenDetail = (ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setItNotes(ticket.itNotes || '');
    setIsModalOpen(true);
  };

  // บันทึกการอัปเดตสถานะ (IT Update)
  const handleUpdateTicket = async (e) => {
    e.preventDefault();
    if (!selectedTicket) return;

    const updatedTicket = {
      ...selectedTicket,
      status: newStatus,
      itNotes: itNotes
    };

    // อัปเดตในสเตท
    const updatedTickets = tickets.map(t => t.id === selectedTicket.id ? updatedTicket : t);
    setTickets(updatedTickets);
    setSelectedTicket(updatedTicket);

    try {
      // 🚀 พยายามส่งการอัปเดตไปยังโฮสต์
      await fetch(`${API_URL}?action=update_ticket_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedTicket.id, status: newStatus, itNotes })
      });
    } catch (err) {
      // 🚀 อัปเดตลง LocalStorage แทน
      localStorage.setItem('helpdesk_tickets', JSON.stringify(updatedTickets));
    }

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'อัปเดตสถานะตั๋วแจ้งปัญหาเรียบร้อยแล้ว!',
      showConfirmButton: false,
      timer: 2000
    });
    setIsModalOpen(false);
  };

  // ลบตั๋ว (ทำลายหลักฐาน/สิทธิ์แอดมิน)
  const handleDeleteTicket = (id, e) => {
    e.stopPropagation();
    Swal.fire({
      title: 'ยืนยันการลบตั๋ว?',
      text: `คุณต้องการลบตั๋วรหัส ${id} หรือไม่?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'ลบตั๋วนี้',
      cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        const updated = tickets.filter(t => t.id !== id);
        setTickets(updated);
        localStorage.setItem('helpdesk_tickets', JSON.stringify(updated));
        Swal.fire('สำเร็จ', 'ลบประวัติตั๋วแล้ว', 'success');
      }
    });
  };

  return (
    <div className="space-y-6 pb-10 animate-fade-in text-gray-800 dark:text-gray-100">
      
      {/* ส่วนหัว */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black flex items-center gap-2">
            🎫 ประวัติตั๋วแจ้งปัญหาไอที (IT Tickets)
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            ติดตามสถานะการแจ้งบั๊ก ปัญหาใช้งานระบบ หรือความคืบหน้าของฟังก์ชันที่ขอเพิ่ม
          </p>
        </div>
      </div>

      {/* สรุปสถิติ (Metrics) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1e293b] p-4 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm text-center">
          <p className="text-[10px] font-bold text-gray-400 uppercase">เรื่องที่แจ้งทั้งหมด</p>
          <p className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-1">{totalCount}</p>
        </div>
        <div className="bg-white dark:bg-[#1e293b] p-4 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm text-center">
          <p className="text-[10px] font-bold text-gray-400 uppercase">รอรับเรื่อง</p>
          <p className="text-xl font-black text-yellow-600 dark:text-yellow-400 mt-1">{pendingCount}</p>
        </div>
        <div className="bg-white dark:bg-[#1e293b] p-4 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm text-center">
          <p className="text-[10px] font-bold text-gray-400 uppercase">กำลังดำเนินการ</p>
          <p className="text-xl font-black text-blue-600 dark:text-blue-400 mt-1">{inProgressCount}</p>
        </div>
        <div className="bg-white dark:bg-[#1e293b] p-4 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm text-center">
          <p className="text-[10px] font-bold text-gray-400 uppercase">แก้ไขเสร็จสิ้น</p>
          <p className="text-xl font-black text-green-600 dark:text-green-400 mt-1">{resolvedCount}</p>
        </div>
      </div>

      {/* แถบฟิลเตอร์ */}
      <div className="bg-white dark:bg-[#1e293b] p-4 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm flex flex-col md:flex-row gap-4 transition-colors">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-3 text-gray-400" />
          <input 
            type="text" 
            placeholder="ค้นหาตามชื่อหัวเรื่อง, ชื่อแอดมินผู้ส่ง..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-sm dark:text-white outline-none focus:border-blue-500 transition-colors" 
          />
        </div>

        <div className="flex gap-3">
          {/* ฟิลเตอร์สถานะ */}
          <select 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)} 
            className="p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs font-bold text-gray-700 dark:text-gray-300 outline-none"
          >
            <option value="all">ทุกสถานะตั๋ว</option>
            <option value="pending">รอรับเรื่อง</option>
            <option value="in_progress">กำลังดำเนินการ</option>
            <option value="resolved">เสร็จสิ้น</option>
            <option value="cancelled">ยกเลิก</option>
          </select>

          {/* ฟิลเตอร์หมวดหมู่ */}
          <select 
            value={categoryFilter} 
            onChange={e => setCategoryFilter(e.target.value)} 
            className="p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs font-bold text-gray-700 dark:text-gray-300 outline-none"
          >
            <option value="all">ทุกหมวดหมู่</option>
            <option value="bug">🐛 บั๊กระบบ</option>
            <option value="feature">✨ ขอฟีเจอร์</option>
            <option value="hardware">🔌 ฮาร์ดแวร์</option>
            <option value="access">🔑 บัญชี/สิทธิ์</option>
            <option value="general">❓ คำถามทั่วไป</option>
          </select>
        </div>
      </div>

      {/* ตารางประวัติตั๋ว */}
      <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm overflow-hidden transition-colors">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 text-xs font-bold border-b border-gray-200 dark:border-[#334155] transition-colors">
                <th className="p-4 pl-6 w-28">รหัสตั๋ว</th>
                <th className="p-4">หัวข้อเรื่องที่แจ้งเข้ามา</th>
                <th className="p-4">หมวดหมู่</th>
                <th className="p-4">ผู้แจ้งเรื่อง</th>
                <th className="p-4">ความเร่งด่วน</th>
                <th className="p-4">สถานะ</th>
                <th className="p-4 text-center">จัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-[#334155] transition-colors">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="p-10 text-center text-blue-500 font-bold">กำลังดาวน์โหลดข้อมูลตั๋ว...</td>
                </tr>
              ) : filteredTickets.length > 0 ? (
                filteredTickets.map((ticket) => (
                  <tr 
                    key={ticket.id} 
                    onClick={() => handleOpenDetail(ticket)}
                    className="hover:bg-gray-50 dark:hover:bg-[#131825]/50 transition-colors cursor-pointer group"
                  >
                    <td className="p-4 pl-6 font-bold text-gray-400 text-xs">{ticket.id}</td>
                    <td className="p-4 font-bold text-gray-800 dark:text-white max-w-xs truncate" title={ticket.subject}>
                      {ticket.subject}
                      {ticket.screenshot && (
                        <span className="inline-flex items-center gap-0.5 ml-2 text-[10px] text-blue-500 font-bold bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
                          <FiImage size={10} /> มีรูปภาพ
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-xs font-bold">{getCategoryLabel(ticket.category)}</td>
                    <td className="p-4 text-xs">
                      {ticket.reporterName}
                      <p className="text-[10px] text-gray-400">{new Date(ticket.createdAt).toLocaleDateString('th-TH')}</p>
                    </td>
                    <td className="p-4">{getPriorityBadge(ticket.priority)}</td>
                    <td className="p-4">{getStatusBadge(ticket.status)}</td>
                    <td className="p-4 text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-center items-center gap-1.5">
                        <button 
                          onClick={() => handleOpenDetail(ticket)}
                          className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition"
                          title="ดูรายละเอียด"
                        >
                          <FiChevronRight size={14} />
                        </button>
                        {isITOrOwner && (
                          <button 
                            onClick={(e) => handleDeleteTicket(ticket.id, e)}
                            className="bg-red-50 text-red-500 p-1.5 rounded-lg hover:bg-red-100 transition"
                            title="ลบประวัติ"
                          >
                            <FiTrash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="p-10 text-center text-gray-400 font-semibold">ไม่มีตั๋วแจ้งปัญหาระบบในประวัติ</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. MODAL รายละเอียดตั๋วและการอัปเดตสถานะของฝ่าย IT */}
      {isModalOpen && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in text-gray-800">
          <div className="bg-white dark:bg-[#131825] dark:text-white w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-200 dark:border-[#334155] overflow-hidden max-h-[90vh] flex flex-col justify-between">
            
            {/* Header */}
            <div className="bg-gray-100 dark:bg-[#1e2330] p-4 flex justify-between items-center border-b border-gray-200 dark:border-[#334155]">
              <h3 className="font-black text-gray-800 dark:text-white text-base flex items-center gap-2">
                <span>🛟 รายละเอียดตั๋วรหัส:</span>
                <span className="text-indigo-600 dark:text-indigo-400">{selectedTicket.id}</span>
              </h3>
              <button 
                onClick={() => setIsModalOpen(false)} 
                className="text-gray-500 hover:text-red-500 transition p-1"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Content (เลื่อนขึ้นลงได้) */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left">
              
              {/* ข้อมูลทั่วไป */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pb-3 border-b border-gray-200 dark:border-[#334155]">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">ประเภทตั๋ว</p>
                  <p className="font-bold text-xs mt-0.5">{getCategoryLabel(selectedTicket.category)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">ผู้แจ้งเรื่อง</p>
                  <p className="font-bold text-xs mt-0.5 truncate" title={selectedTicket.reporterEmail}>
                    {selectedTicket.reporterName}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">ความเร่งด่วน</p>
                  <p className="mt-0.5">{getPriorityBadge(selectedTicket.priority)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">วันที่ส่งเรื่อง</p>
                  <p className="font-bold text-xs mt-0.5 text-gray-500">
                    {new Date(selectedTicket.createdAt).toLocaleDateString('th-TH')}
                  </p>
                </div>
              </div>

              {/* หัวข้อ */}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase">หัวข้อเรื่อง</p>
                <h2 className="text-base font-black text-indigo-600 dark:text-indigo-400 mt-1">{selectedTicket.subject}</h2>
              </div>

              {/* รายละเอียด */}
              <div className="bg-gray-50 dark:bg-[#0f172a]/50 p-4 rounded-xl border border-gray-100 dark:border-[#334155]">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">รายละเอียดปัญหาการใช้งาน</p>
                <p className="text-xs text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                  {selectedTicket.description}
                </p>
              </div>

              {/* รูปภาพแนบ (ถ้ามี) */}
              {selectedTicket.screenshot && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">สกรีนช็อตภาพหน้าจอที่แนบมา</p>
                  <div 
                    onClick={() => setScreenshotModal(selectedTicket.screenshot)}
                    className="relative max-w-sm rounded-xl overflow-hidden border border-gray-200 dark:border-[#334155] cursor-pointer group shadow-sm hover:shadow transition"
                  >
                    <img 
                      src={selectedTicket.screenshot} 
                      alt="Attached Screenshot" 
                      className="w-full h-auto object-cover max-h-40"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-bold transition-opacity">
                      🔍 คลิกเพื่อดูภาพขยาย
                    </div>
                  </div>
                </div>
              )}

              {/* ฟอร์มอัปเดตตั๋วแจ้งปัญหา (ฝั่ง IT) */}
              {isITOrOwner ? (
                <form onSubmit={handleUpdateTicket} className="pt-4 border-t border-gray-200 dark:border-[#334155] space-y-4">
                  <h4 className="text-xs font-black text-indigo-500 uppercase">⚙️ การตอบกลับและอัปเดตตั๋ว (สำหรับไอที)</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] text-gray-400">เปลี่ยนสถานะตั๋ว</label>
                      <select 
                        value={newStatus}
                        onChange={e => setNewStatus(e.target.value)}
                        className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs font-bold text-gray-700 dark:text-gray-300"
                      >
                        <option value="pending">รอรับเรื่อง (Pending)</option>
                        <option value="in_progress">กำลังดำเนินการ (In Progress)</option>
                        <option value="resolved">เสร็จสิ้น (Resolved)</option>
                        <option value="cancelled">ยกเลิกตั๋ว (Cancelled)</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-gray-400">บันทึกความคืบหน้าของนักพัฒนา / เจ้าหน้าที่ไอที (IT Notes)</label>
                    <textarea 
                      placeholder="กรอกบันทึกทางเทคนิค เช่น รับเรื่องและกำลังแก้โค้ดหลังบ้าน หรือ แก้ไขเสร็จสิ้นในอัปเดตรอบล่าสุด..."
                      value={itNotes}
                      onChange={e => setItNotes(e.target.value)}
                      rows="3"
                      className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-xs dark:text-white outline-none focus:border-blue-500 transition-colors resize-none"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button 
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 text-gray-800 dark:text-white font-bold rounded-lg text-xs transition"
                    >
                      ปิดหน้าต่าง
                    </button>
                    <button 
                      type="submit"
                      className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-xs shadow transition"
                    >
                      บันทึกอัปเดตตั๋ว
                    </button>
                  </div>
                </form>
              ) : (
                <div className="pt-4 border-t border-gray-200 dark:border-[#334155] space-y-4 text-left">
                  <h4 className="text-xs font-black text-gray-400 uppercase">💬 ข้อความตอบกลับจากเจ้าหน้าที่ไอที</h4>
                  {selectedTicket.itNotes ? (
                    <div className="bg-indigo-50/50 dark:bg-indigo-950/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-900/40 text-xs">
                      <p className="text-indigo-800 dark:text-indigo-300 font-bold mb-1">📝 ความคืบหน้า:</p>
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedTicket.itNotes}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">ยังไม่มีข้อความตอบกลับหรือการประเมินจากเจ้าหน้าที่ไอทีในขณะนี้</p>
                  )}
                  <button 
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="w-full py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 text-gray-800 dark:text-white font-bold rounded-lg text-xs transition"
                  >
                    ปิดหน้าต่าง
                  </button>
                </div>
              )}

            </div>

          </div>
        </div>
      )}

      {/* 5. MODAL ดูรูปภาพขนาดใหญ่ */}
      {screenshotModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 cursor-pointer"
          onClick={() => setScreenshotModal(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img 
              src={screenshotModal} 
              alt="Fullscreen Screenshot" 
              className="w-full h-auto max-h-[85vh] rounded-xl object-contain border-2 border-white/20 shadow-2xl"
            />
            <button 
              onClick={() => setScreenshotModal(null)}
              className="absolute top-2 right-2 p-2 bg-black/60 text-white rounded-full hover:bg-black/80 transition"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
